"use client";

import React from "react";
import { observer } from "mobx-react-lite";
import StrongSymbolRow from "./StrongSymbolRow";
import { useStore } from "../services/store";
import {
  filterSymbolsByMarketCap,
  groupSymbolsBySector,
} from "./util-symbol-grouping";

function normalizeTopGroups(top) {
  if (Array.isArray(top)) return top;
  if (!top || typeof top !== "object") return [];

  return Object.entries(top).map(([sector, symbols]) => ({
    sector,
    symbols: Array.isArray(symbols) ? symbols : [],
  }));
}

export function getTopSectorSymbolCount(top) {
  return normalizeTopGroups(top).reduce(
    (sum, group) => sum + (group.symbols?.length || 0),
    0,
  );
}

function getTopPercentChange(item) {
  const value = Number(item?.percentChange);
  return Number.isFinite(value) ? value : 0;
}

function sortTopEntries(entries) {
  return [...entries].sort(
    ([, a], [, b]) => getTopPercentChange(b) - getTopPercentChange(a),
  );
}

function sortTopGroupsByStrength(grouped) {
  return Object.entries(grouped).sort(([, entriesA], [, entriesB]) => {
    const maxA = Math.max(
      ...entriesA.map(([, item]) => getTopPercentChange(item)),
    );
    const maxB = Math.max(
      ...entriesB.map(([, item]) => getTopPercentChange(item)),
    );
    return maxB - maxA;
  });
}

function SubsectorHeader({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: "rgba(255,255,255,0.6)",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        marginBottom: 4,
        paddingLeft: 4,
        borderLeft: "2px solid rgba(255,255,255,0.2)",
      }}
    >
      {children}
    </div>
  );
}

function TopSymbolRow({ sector, item }) {
  return (
    <StrongSymbolRow
      key={`${sector}-${item.symbol}`}
      sym={item.symbol}
      val={{
        sector: item.sector || sector,
        pattern: item.pattern || "",
        percentChange: item.percentChange,
      }}
      isPos
      colCh={10}
      source="top"
    />
  );
}

export default observer(function TopSectorPanel({ data }) {
  const store = useStore();
  const displayGroups = normalizeTopGroups(data?.top);

  if (!displayGroups.length) {
    return (
      <div
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 12,
          color: "#9ca3af",
          padding: "8px 2px",
        }}
      >
        (no top symbols over 5%)
      </div>
    );
  }

  return (
    <div
      className="font-mono text-sm whitespace-pre leading-5 near-white bg-near-black"
      style={{
        marginBottom: 8,
        borderLeft: "3px solid rgba(34,197,94,.9)",
        padding: 8,
        paddingLeft: 12,
        borderTopLeftRadius: 8,
        borderBottomLeftRadius: 8,
        backgroundColor: "rgba(20,83,45,.3)",
        boxShadow: "0 2px 6px rgba(0,0,0,.25)",
      }}
    >
      {displayGroups.map(({ sector, symbols }, groupIndex) => {
        const rawEntries = (symbols || [])
          .filter((item) => item?.symbol)
          .map((item) => [String(item.symbol).toLowerCase(), item]);
        const filteredEntries = store.isMarketCapFilter
          ? filterSymbolsByMarketCap(rawEntries, store.marketCapMap)
          : rawEntries;
        const grouped = groupSymbolsBySector(filteredEntries);
        const groupEntries = sortTopGroupsByStrength(grouped);
        if (!groupEntries.length) return null;

        return (
          <div key={sector}>
            {groupIndex > 0 && <div style={{ height: 10 }} />}

            {groupEntries.map(([sectorKey, sectorSymbols], childIndex) => (
              <div key={`${sector}-${sectorKey}`}>
                {childIndex > 0 && <div style={{ height: 8 }} />}
                <SubsectorHeader>{sectorKey}</SubsectorHeader>
                {sortTopEntries(sectorSymbols).map(([, item]) => (
                  <TopSymbolRow
                    key={`${sector}-${sectorKey}-${item.symbol}`}
                    sector={sectorKey || sector}
                    item={item}
                  />
                ))}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
});
